import { getContrast, parseToRgba } from 'color2k';

import type { ThemedValue } from '@creditornot/cb-utils';
import { createThemedCssVarDefinitions } from '@creditornot/cb-utils';

import type * as color from '../color';

import { adjustLuminanceForContrast } from './adjustLuminanceForContrast';
import { modifiers, pipeModifiers } from './modifiers';
import { readThemedValue } from './readThemedValue';

type ColorVar = keyof typeof color;

type Theme = Record<ColorVar, string | ThemedValue>;

interface Options {
  /**
   * The primary (brand) color in hex or rgba format.
   */
  brandColor: string;
  /**
   * By setting this option to `true`, the brand color will be automatically adjusted to ensure
   * that it meets the minimum contrast ratio of 4.5:1 with the background color.
   *
   * Note that for the `lightHighContrast` mode, the contrast ratio is 7:1 when this is enabled and 4.5:1 when it's disabled.
   *
   * @default false
   */
  ensureSufficientContrast?: boolean;
}

/**
 * Generates design tokens based on the given brand color.
 */
export const generateThemeOverride = ({
  brandColor,
  ensureSufficientContrast = false,
}: Options) => {
  if (parseToRgba(brandColor)[3] < 1) throw new Error('Primary color must be opaque.');

  const bgFillBrand: ThemedValue = {
    light: ensureSufficientContrast
      ? adjustLuminanceForContrast('#ffffff', brandColor, 4.5)
      : brandColor,
    lightHighContrast: adjustLuminanceForContrast(
      '#ffffff',
      brandColor,
      ensureSufficientContrast ? 7 : 4.5,
    ),
    dark: ensureSufficientContrast
      ? adjustLuminanceForContrast('#202125', brandColor, 4.5)
      : brandColor,
  };

  const bgSurfaceBrand = pipeModifiers(
    modifiers.alpha({ light: 0.08, dark: 0.16 }),
    modifiers.elevateOnDarkMode,
    modifiers.solidify,
  )(ensureSufficientContrast ? bgFillBrand : brandColor);

  const textBrand: ThemedValue = {
    light: ensureSufficientContrast
      ? adjustLuminanceForContrast(readThemedValue(bgSurfaceBrand, 'light'), brandColor, 4.5)
      : brandColor,
    dark: ensureSufficientContrast
      ? adjustLuminanceForContrast(readThemedValue(bgSurfaceBrand, 'dark'), brandColor, 4.5)
      : brandColor,
    lightHighContrast: adjustLuminanceForContrast(
      readThemedValue(bgSurfaceBrand, 'lightHighContrast'),
      brandColor,
      ensureSufficientContrast ? 7 : 4.5,
    ),
  };

  const textOnBgFillBrand: ThemedValue = {
    light: '#ffffff',
    // flip text color if contrast gets too low, even if it's not a high contrast mode
    dark:
      getContrast(readThemedValue(bgFillBrand, 'dark'), '#202125') >= 2.5 ? '#202125' : '#ffffff',
  };

  // See https://www.figma.com/file/r2FVoD8RC0vgPMxL4FoI42/Tokens-playground?type=design&node-id=784-358&mode=design&t=bRMkEDVASrIiiqLc-11
  // for specifications on how dynamic colors are derived.
  const varMap = {
    // brand fill
    bgFillBrand,
    bgFillBrandHovered: pipeModifiers(
      modifiers.alpha({ light: 0.88, dark: 0.88 }),
      modifiers.solidify,
    )(bgFillBrand),
    bgFillBrandPressed: pipeModifiers(
      modifiers.alpha({ light: 0.8, dark: 0.8 }),
      modifiers.solidify,
    )(bgFillBrand),
    bgFillBrandSelected: pipeModifiers(
      modifiers.alpha({ light: 0.84, dark: 0.84 }),
      modifiers.solidify,
    )(bgFillBrand),
    bgFillBrandDisabled: pipeModifiers(
      modifiers.alpha({ light: 0.64, dark: 0.64 }),
      modifiers.solidify,
    )(bgFillBrand),

    // text brand
    textBrand,
    textBrandHovered: pipeModifiers(
      modifiers.alpha({ light: 0.92, dark: 0.92 }),
      modifiers.solidify,
    )(textBrand),
    textBrandPressed: pipeModifiers(
      modifiers.alpha({ light: 0.8, dark: 0.8 }),
      modifiers.solidify,
    )(textBrand),
    textBrandSelected: pipeModifiers(
      modifiers.alpha({ light: 0.88, dark: 0.88 }),
      modifiers.solidify,
    )(textBrand),
    textBrandDisabled: pipeModifiers(
      modifiers.alpha({ light: 0.64, dark: 0.64 }),
      modifiers.solidify,
    )(textBrand),

    // surface brand
    bgSurfaceBrand,
    bgSurfaceBrandHovered: pipeModifiers(
      modifiers.alpha({ light: 0.16, dark: 0.24 }),
      modifiers.elevateOnDarkMode,
      modifiers.solidify,
    )(brandColor),
    bgSurfaceBrandPressed: pipeModifiers(
      modifiers.alpha({ light: 0.24, dark: 0.32 }),
      modifiers.elevateOnDarkMode,
      modifiers.solidify,
    )(brandColor),
    bgSurfaceBrandSelected: pipeModifiers(
      modifiers.alpha({ light: 0.12, dark: 0.2 }),
      modifiers.elevateOnDarkMode,
      modifiers.solidify,
    )(brandColor),
    bgSurfaceBrandDisabled: pipeModifiers(
      modifiers.alpha({ light: 0.04, dark: 0.12 }),
      modifiers.elevateOnDarkMode,
      modifiers.solidify,
    )(brandColor),

    // text on brand
    textOnBgFillBrand: textOnBgFillBrand,
    textOnBgFillBrandHovered: textOnBgFillBrand,
    textOnBgFillBrandPressed: textOnBgFillBrand,
    textOnBgFillBrandSelected: textOnBgFillBrand,
    textOnBgFillBrandDisabled: pipeModifiers(
      modifiers.alpha({ light: 0.4, dark: 0.4 }),
      modifiers.toHex,
    )(textOnBgFillBrand),

    // other
    borderSelected: bgFillBrand,
  } satisfies Partial<Theme>;

  return varMap;
};

/**
 * Generates CSS for design tokens based on the given primary (brand) color.
 */
export const generateThemeOverrideCss = (options: Options) =>
  createThemedCssVarDefinitions(generateThemeOverride(options), {
    kebabCase: true,
    prefix: 'cb-color',
    selector: ':root',
  });
